// Caminho absoluto: src/utils/assetUrl.ts

/**
 * Monta a URL pública de um asset hospedado no Firebase Storage.
 *
 * IMPORTANTE — por que não `storage.googleapis.com`:
 * o bucket não tem ACL pública (o upload em scripts/uploadImagesToStorage.js não usa
 * `predefinedAcl: 'publicRead'`), então o endpoint nativo do GCS responde 403.
 * Quem libera a leitura é a Storage Rule, e ela só vale no endpoint de download do
 * Firebase, que exige o path inteiro URL-encoded (as barras viram %2F).
 *
 *   ❌ https://storage.googleapis.com/{bucket}/quiz-dos-imortais/assets/a/b.webp     → 403
 *   ✅ https://firebasestorage.googleapis.com/v0/b/{bucket}/o/quiz...%2Fb.webp?alt=media
 */

const BUCKET = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET?.trim();

/** Prefixo canônico gravado por scripts/uploadImagesToStorage.js. */
export const STORAGE_PREFIX = 'quiz-dos-imortais/assets/';

/**
 * @param relativePath Caminho relativo a `upload/assets/`, ex.: `desafios/rosto/didi.webp`.
 *                     Aceita também o caminho já prefixado (idempotente).
 * @returns URL absoluta, ou `null` se não houver bucket configurado / path vazio.
 */
export function buildAssetUrl(relativePath?: string | null): string | null {
  if (!relativePath || !BUCKET) return null;

  const clean = relativePath
    .trim()
    .replace(/^\/+/, '')
    .replace(/^quiz-dos-imortais\/assets\//, '');

  if (!clean) return null;

  const objectPath = encodeURIComponent(`${STORAGE_PREFIX}${clean}`);
  return `https://firebasestorage.googleapis.com/v0/b/${BUCKET}/o/${objectPath}?alt=media`;
}

/** URL do retrato de um Imortal a partir do `imagem_slug` do catálogo. */
export function buildRetratoUrl(imagemSlug?: string | null): string | null {
  if (!imagemSlug) return null;
  return buildAssetUrl(`desafios/rosto/${imagemSlug}.webp`);
}

/**
 * Dispara o download da imagem em background (cache do browser), sem bloquear.
 * Usado no início da fase para que o retrato já esteja pronto quando a tela montar.
 */
export function preloadImage(url?: string | null): void {
  if (!url || typeof Image === 'undefined') return;
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
}
